import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { ArrowRight } from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * Clean horizontal CTA banner — ~1350×280 ratio.
 * Logo (left) · Headline + description aligned LEFT (center) · Stacked buttons (right).
 * Hotel image lives ONLY on the right edge as a soft background accent.
 */
export default function FinalCTA() {
  const t = useT();
  return (
    <section className="relative bg-white py-14 md:py-20" data-testid="final-cta">
      <div className="container-akm">
        <div className="relative overflow-hidden rounded-[24px] border border-[#F1E5D7] bg-white shadow-[0_24px_60px_-24px_rgba(15,23,42,0.18)] md:rounded-[28px]">
          {/* Right-side soft image accent (NOT a full background blur) */}
          <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[42%] md:block">
            <img
              src="https://images.unsplash.com/photo-1582610116397-edb318620f90?auto=format&fit=crop&w=1200&q=75"
              alt=""
              aria-hidden
              loading="lazy"
              decoding="async"
              className="h-full w-full select-none object-cover opacity-55"
            />
            {/* Smooth fade from white → image, so text on the left sits on clean white */}
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/70 to-white/0" aria-hidden />
            {/* Tiny orange wash near the buttons for warmth */}
            <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(60%_70%_at_70%_50%,rgba(255,122,0,0.10),transparent_70%)]" aria-hidden />
          </div>

          {/* Content grid — left-aligned, three columns */}
          <div className="relative grid items-center gap-6 px-6 py-8 md:gap-10 md:px-10 md:py-10 lg:grid-cols-[auto_1fr_auto] lg:gap-12 lg:px-12">
            {/* LEFT — large logo tile */}
            <div className="flex justify-center lg:justify-start">
              <div className="grid h-[130px] w-[130px] place-items-center rounded-[26px] border border-[#F1E5D7] bg-white shadow-[0_18px_38px_-14px_rgba(15,23,42,0.22)] md:h-[150px] md:w-[150px]">
                <img
                  src="/akomoda-icon.png"
                  alt="Akomoda"
                  className="h-[100px] w-[100px] object-contain md:h-[118px] md:w-[118px]"
                  loading="lazy"
                  decoding="async"
                  width="118"
                  height="118"
                />
              </div>
            </div>

            {/* CENTER — headline + description, LEFT aligned */}
            <div className="text-left">
              <h2
                className="font-display font-extrabold tracking-[-0.03em] text-[#0F172A]"
                style={{ fontSize: "clamp(1.55rem, 1rem + 1.4vw, 2.1rem)", lineHeight: 1.14 }}
              >
                Transformoni Hotelin Tuaj me{" "}
                <span className="text-[#F26322]">Akomoda</span>
              </h2>
              <p className="mt-3 max-w-xl text-[14px] leading-[1.7] text-[#475569] md:text-[14.5px]">
                Automatizoni rezervimet, menaxhoni dhomat, kontrolloni financat dhe rrisni
                performancën e hotelit tuaj me një platformë moderne të ndërtuar për hotelerinë
                e sotme.
              </p>
            </div>

            {/* RIGHT — stacked buttons */}
            <div className="flex flex-col items-stretch gap-3 lg:min-w-[210px]">
              <Link
                to="/demo"
                data-testid="final-demo"
                className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-[#F26322] px-7 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white shadow-[0_12px_28px_-10px_rgba(242,99,34,0.55)] transition-all hover:bg-[#EA580C] hover:shadow-[0_16px_36px_-10px_rgba(242,99,34,0.7)]"
              >
                {t("Kërko Demo", "Request Demo")}
                <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/kontakt"
                data-testid="final-contact"
                className="inline-flex items-center justify-center rounded-2xl border border-[#E2E8F0] bg-white px-7 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-[#0F172A] shadow-[0_8px_22px_-12px_rgba(15,23,42,0.2)] transition-all hover:border-[#CBD5E1] hover:bg-[#FAFBFC] hover:shadow-[0_12px_30px_-12px_rgba(15,23,42,0.3)]"
              >
                {t("Na Kontakto", "Contact us")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
